// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { useCallback, useEffect, useMemo, useState } from 'react'
import { getVirtualDeviceId } from '../common/virtualDevice'
import type { ExportRangeMode } from '../components/ReportDeviceSelector'
import {
  type DeviceWearRow,
  type ExportSelectionMode,
  exportSelectionMode,
  selectedRowsInWearOrder,
  selectionWindow,
} from '../utils/deviceWearPeriods'
import { useDeviceWearPeriods } from './useDeviceWearPeriods'

interface UseExportSelectionArgs {
  petId: string | number
  /** The device the page is showing — used until the vet opens the dialog. */
  deviceId: string | number
  /** The page's window — used until the vet opens the dialog, and as the custom default. */
  defaultStart: Date
  defaultEnd: Date
}

export interface ExportSelection {
  /** Call when the export dialog opens: loads the device list once. */
  activate: () => void
  mode: ExportRangeMode
  setMode: (mode: ExportRangeMode) => void
  rows: DeviceWearRow[]
  rowsLoading: boolean
  rowsError: unknown
  selectedIds: Array<string | number>
  setSelectedIds: (ids: Array<string | number>) => void
  customStart: Date
  customEnd: Date
  setCustomRange: (start: Date, end: Date) => void
  /** The pet's whole data range, from the device list (null until known). */
  petDataRange: { startDate: Date; endDate: Date } | null
  /** What to fetch and report on. */
  queryDeviceId: string | number
  queryDeviceIds: Array<string | number> | undefined
  start: Date
  end: Date
  /** Selected devices, oldest wear first — for the report header. */
  selectedRows: DeviceWearRow[]
  selectionMode: ExportSelectionMode | null
  /** True when the dialog's choice (not the page's window) drives the export. */
  active: boolean
}

/**
 * Export range state shared by the PDF and CSV exports (vic/vetpo#56), so the
 * two follow one rule: device selection first (most recent sensor preselected),
 * custom dates as the fallback. Until the dialog is opened the page's own
 * device + window are used unchanged, so nothing extra is fetched on page load.
 */
export function useExportSelection({
  petId,
  deviceId,
  defaultStart,
  defaultEnd,
}: UseExportSelectionArgs): ExportSelection {
  const [activated, setActivated] = useState(false)
  const [mode, setMode] = useState<ExportRangeMode>('devices')
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([])
  const [initialized, setInitialized] = useState(false)
  const [customStart, setCustomStart] = useState<Date>(defaultStart)
  const [customEnd, setCustomEnd] = useState<Date>(defaultEnd)

  // Follow the PATIENT (L-007, eng-brain P69-1). PetShow keeps these export
  // buttons mounted across /pets/A/show -> /pets/B/show, so without this pet
  // B's dialog opened with pet A's sensor ticked, the preselect never re-ran,
  // and the audit row + CSV filename named pet A's sensor against pet B.
  // Reset during render (not in an effect) so no render sees the stale state.
  const [forPet, setForPet] = useState(String(petId))
  if (forPet !== String(petId)) {
    setForPet(String(petId))
    setActivated(false)
    setMode('devices')
    setSelectedIds([])
    setInitialized(false)
    setCustomStart(defaultStart)
    setCustomEnd(defaultEnd)
  }

  const { rows, isLoading: rowsLoading, error: rowsError } = useDeviceWearPeriods(petId, activated)

  // Preselect the most recent device once the list arrives; a pet with no
  // device data (or a failed list) falls back to the custom range.
  // `rowsError` must gate this: one failed edge query leaves that sensor with a
  // null first/last, so `rows.find` would silently tick an OLDER sensor while
  // ReportDeviceSelector hides the list behind "Could not load the sensor list.
  // Use a custom date range instead." — an invisible, exportable selection.
  useEffect(() => {
    if (!activated || initialized || rowsLoading) return
    const newest = rowsError ? undefined : rows.find((r) => r.first && r.last)
    if (newest) {
      setSelectedIds([newest.id])
      setMode('devices')
    } else {
      setMode('custom')
    }
    setInitialized(true)
  }, [activated, initialized, rowsLoading, rows, rowsError])

  // An INCOMPLETE list cannot bound the pet's data: on a partial failure the
  // sensors that did load gave the span of an OLDER sensor, and the custom-range
  // pickers (min/max, presets, "Data available") were clamped to it, so the
  // newest sensor's days could not be picked (eng-brain pass 78). Unknown = null:
  // no clamp, and the PDF falls back to its own range estimate.
  const petDataRange = useMemo(() => {
    if (rowsError) return null
    const withData = rows.filter((r) => r.first && r.last)
    if (!withData.length) return null
    return {
      startDate: new Date(Math.min(...withData.map((r) => (r.first as Date).getTime()))),
      endDate: new Date(Math.max(...withData.map((r) => (r.last as Date).getTime()))),
    }
  }, [rows, rowsError])

  const deviceWindow = useMemo(() => selectionWindow(rows, selectedIds), [rows, selectedIds])
  const selectedRows = useMemo(
    () => selectedRowsInWearOrder(rows, selectedIds),
    [rows, selectedIds],
  )

  // While the sensor list has FAILED, "By sensor" cannot be offered: the selector
  // replaces the list with "Could not load the sensor list. Use a custom date
  // range instead." A mode the vet cannot act on must not drive the export, and
  // canExport / canConfirm read this value — so derive it here, once, and never
  // let setMode('devices') (the radio, or a stale choice) win over the failure.
  const effectiveMode: ExportRangeMode = rowsError ? 'custom' : mode

  const byDevice = activated && initialized && effectiveMode === 'devices' && deviceWindow !== null
  const byCustom = activated && initialized && effectiveMode === 'custom'
  const active = byDevice || byCustom

  const setCustomRange = useCallback((start: Date, end: Date) => {
    setCustomStart(start)
    setCustomEnd(end)
  }, [])

  return {
    activate: useCallback(() => setActivated(true), []),
    mode: effectiveMode,
    setMode,
    rows,
    rowsLoading,
    rowsError,
    selectedIds,
    setSelectedIds,
    customStart,
    customEnd,
    setCustomRange,
    petDataRange,
    // By device: the pet's virtual "All Devices" restricted to the ticked
    // subset (overlaps resolved among the ticked sensors only). Custom: every
    // device of the pet. Before the dialog: the page's own device.
    queryDeviceId: active ? getVirtualDeviceId(petId) : deviceId,
    queryDeviceIds: byDevice ? selectedIds : undefined,
    start: byDevice
      ? (deviceWindow as { start: Date }).start
      : byCustom
        ? customStart
        : defaultStart,
    end: byDevice ? (deviceWindow as { end: Date }).end : byCustom ? customEnd : defaultEnd,
    selectedRows: byDevice ? selectedRows : [],
    selectionMode: active ? exportSelectionMode(effectiveMode, selectedIds) : null,
    active,
  }
}
